import React from "react";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import { getAuthCredentials } from "../../utils/auth";

const SalesExport = ({ user, dateRange, activeTab }) => {
  const { token } = getAuthCredentials();

  const exportToPDF = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/sales/export`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            startDate: dateRange[0].format("YYYY-MM-DD"),
            endDate: dateRange[1].format("YYYY-MM-DD"),
            posId: user.posId,
            tab: activeTab === 0 ? "summary" : "transactions",
          },
        }
      );

      const doc = new jsPDF();
      const { header, summary, transactions } = data;

      doc.setFontSize(10);
      doc.text(`Generated By: ${header.generatedBy}`, 10, 10);
      doc.text(`Company: ${header.company}`, 10, 15);
      doc.text(`POS ID: ${header.posId}`, 10, 20);
      doc.text(`Generated On: ${header.generatedOn}`, 10, 25);
      doc.text(`Date Covered: ${header.dateCovered}`, 10, 30);

      if (activeTab === 1 && transactions?.records?.length) {
        doc.addPage();
        doc.setFontSize(12);
        doc.text("Transaction Logs", 10, 10);
        autoTable(doc, {
          startY: 15,
          head: [["Date", "Description", "Type", "Original", "Discounted"]],
          body: transactions.records.map((r) => [
            new Date(r.date).toLocaleString("en-PH", {
              timeZone: "Asia/Manila",
              hour12: true,
            }),
            r.description,
            r.salesType,
            `₱${(r.originalPrice || 0).toFixed(2)}`,
            `₱${(r.price || 0).toFixed(2)}`,
          ]),
        });
      }

      if (activeTab === 0 && summary) {
        doc.addPage();
        doc.setFontSize(12);
        doc.text("Sales Summary", 10, 10);
        autoTable(doc, {
          startY: 15,
          head: [["Metric", "Current", "Previous"]],
          body: [
            [
              "Gross Sales",
              summary.statistics.current.grossSales,
              summary.statistics.previous.grossSales,
            ],
            [
              "Net Sales Without VAT",
              summary.statistics.current.netSalesWithoutVat,
              summary.statistics.previous.netSalesWithoutVat,
            ],
            [
              "Discounts",
              summary.statistics.current.totalDiscount,
              summary.statistics.previous.totalDiscount,
            ],
            [
              "Net With Discounts",
              summary.statistics.current.netSalesWithDiscounts,
              summary.statistics.previous.netSalesWithDiscounts,
            ],
            [
              "Transactions",
              summary.statistics.current.totalTransactions,
              summary.statistics.previous.totalTransactions,
            ],
          ],
        });

        doc.addPage();
        doc.text("Sales by Menu Item", 10, 10);
        autoTable(doc, {
          startY: 15,
          head: [["Item", "Current", "Previous"]],
          body: summary.menuSalesComparison.map((item) => [
            item.name,
            `₱${item.current.toFixed(2)}`,
            `₱${item.previous.toFixed(2)}`,
          ]),
        });

        doc.addPage();
        doc.text("Most Popular Items", 10, 10);
        autoTable(doc, {
          startY: 15,
          head: [["Item", "Qty", "Current ₱", "Previous ₱"]],
          body: summary.mostPopularItems.map((item) => [
            item.name,
            item.qty.current,
            `₱${item.amount.current.toFixed(2)}`,
            `₱${item.amount.previous.toFixed(2)}`,
          ]),
        });

        doc.addPage();
        doc.text("Least Popular Items", 10, 10);
        autoTable(doc, {
          startY: 15,
          head: [["Item", "Qty", "Current ₱", "Previous ₱"]],
          body: summary.leastPopularItems.map((item) => [
            item.name,
            item.qty.current,
            `₱${item.amount.current.toFixed(2)}`,
            `₱${item.amount.previous.toFixed(2)}`,
          ]),
        });

        doc.addPage();
        doc.text("Sales by Category", 10, 10);
        autoTable(doc, {
          startY: 15,
          head: [["Category", "Qty", "Current ₱", "Previous ₱"]],
          body: summary.popularPerCategory.map((cat) => [
            cat.name,
            cat.qty.current,
            `₱${cat.amount.current.toFixed(2)}`,
            `₱${cat.amount.previous.toFixed(2)}`,
          ]),
        });

        doc.addPage();
        doc.text("Hourly Sales (Average)", 10, 10);
        autoTable(doc, {
          startY: 15,
          head: [["Hour", "Current", "Previous"]],
          body: summary.hourlySalesComparison.map((row) => [
            `${row.hour}:00`,
            `₱${row.current.toFixed(2)}`,
            `₱${row.previous.toFixed(2)}`,
          ]),
        });

        doc.addPage();
        doc.text("Operating Hours", 10, 10);
        autoTable(doc, {
          startY: 15,
          head: [["Date", "Open", "Close", "Hours"]],
          body: summary.operatingHours.map((row) => [
            row.date,
            row.openTime,
            row.closeTime,
            row.operatingHours,
          ]),
        });

        doc.addPage();
        doc.text("Average Order Time per Day", 10, 10);
        autoTable(doc, {
          startY: 15,
          head: [["Date", "Avg Minutes"]],
          body: summary.averageOrderTime.current.perDay.map((row) => [
            row.date,
            row.avgMinutes,
          ]),
        });

        doc.addPage();
        doc.text("Customer Average Spending", 10, 10);
        autoTable(doc, {
          startY: 15,
          head: [["Date", "Average Spend"]],
          body: summary.customerAverageSpending.current.perDay.map((row) => [
            row.date,
            `₱${row.avg.toFixed(2)}`,
          ]),
        });
      }

      const filename = `${dateRange[0].format(
        "YYYY-MM-DD"
      )}_to_${dateRange[1].format("YYYY-MM-DD")}-${
        user.companyname
      }-SalesReport.pdf`;
      doc.save(filename);
    } catch (err) {
      console.error("Failed to export PDF:", err);
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mb: 4 }}>
      <Tooltip title="Coming Soon">
        <span>
          <Button
            variant="contained"
            color="error"
            startIcon={<PictureAsPdfIcon />}
            onClick={exportToPDF}
            disabled
          >
            Export to PDF
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
};

export default SalesExport;
